import React, { useState, useEffect } from 'react';
import { generateTxnRef, computeKeyTest, formatUSD, todayISO, KTT_CODE, COMPANY, toUSD, getFXRate, formatFXAmount } from '../utils/keyGenerator';

// Steps: 1=API CONFIG, 2=PAYMENT DETAILS, 3=REVIEW, 4=CONFIRMED
export default function SendAPI({ wallet, addTransaction, setSlip, accounts, preSelectedAccount, onAccountUsed }) {
  const [step, setStep] = useState(1);
  const [sourceAccountIban, setSourceAccountIban] = useState(preSelectedAccount?.iban || '');
  const [form, setForm] = useState({
    beneficiary: "", accountNo: "", bankName: "", bankBIC: "",
    currency: "USD", amount: "", narrative: "", valueDate: todayISO()
  });
  const [apiConfig, setApiConfig] = useState({
    endpoint: "", baseURL: "", serverIP: "",
    authMethod: "Bearer Token", apiKey: "", privateKey: "",
    webhookURL: "", callbackURL: "", timeout: "30",
    contentType: "application/json", protocol: "HTTPS",
  });
  const [apiSaved, setApiSaved] = useState(false);
  const [alert, setAlert] = useState(null);
  const [sending, setSending] = useState(false);
  const [txRef] = useState(() => generateTxnRef("API"));

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const setApi = k => e => { setApiConfig(f => ({ ...f, [k]: e.target.value })); setApiSaved(false); };

  // Auto-select source account when navigating from Account page
  useEffect(() => {
    if (preSelectedAccount?.iban) {
      setSourceAccountIban(preSelectedAccount.iban);
      if (onAccountUsed) onAccountUsed();
    }
  }, [preSelectedAccount, onAccountUsed]);

  const keyTest = form.amount && form.valueDate
    ? computeKeyTest(form.amount, form.valueDate) : "-----";

  // Step 1: Save API config
  const handleSaveApiConfig = () => {
    if (!apiConfig.endpoint && !apiConfig.baseURL) {
      setAlert({ type: "error", msg: "Please fill in at least the API Endpoint URL or Base URL." });
      return;
    }
    setApiSaved(true);
    setAlert({ type: "success", msg: "API endpoint configuration saved. Proceed to fill payment details." });
    setStep(2);
  };

  // Step 2: Validate payment and go to review
  const handleGoToReview = () => {
    if (!form.beneficiary) { setAlert({ type: "error", msg: "Beneficiary is required." }); return; }
    if (!form.accountNo) { setAlert({ type: "error", msg: "Account Number is required." }); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { setAlert({ type: "error", msg: "Please enter a valid amount." }); return; }
    const usdEquiv = toUSD(parseFloat(form.amount), form.currency);
    if (usdEquiv > wallet.balance) { setAlert({ type: "error", msg: `Insufficient balance. ${formatFXAmount(parseFloat(form.amount), form.currency)} = ${formatUSD(usdEquiv)} USD. Available: ${formatUSD(wallet.balance)}` }); return; }
    setAlert(null);
    setStep(3);
  };

  // Back navigation
  const handleBackToApiConfig = () => { setAlert(null); setStep(1); };
  const handleBackToPayment = () => { setAlert(null); setStep(2); };

  // Step 3: Confirm and send
  const handleConfirmSend = async () => {
    setSending(true);
    const amt = parseFloat(form.amount);
    const finalRef = generateTxnRef("API");
    const finalKeyTest = computeKeyTest(amt, form.valueDate);

    const payload = {
      transaction_ref: finalRef,
      sender: COMPANY,
      beneficiary: form.beneficiary,
      account_no: form.accountNo,
      bank: form.bankName,
      bic: form.bankBIC,
      currency: form.currency,
      amount: amt,
      value_date: form.valueDate,
      narrative: form.narrative,
      key_test: finalKeyTest,
      ktt_code: KTT_CODE,
    };

    // Call external API if configured
    if (apiConfig.endpoint) {
      try {
        const headers = { 'Content-Type': apiConfig.contentType || 'application/json' };
        if (apiConfig.authMethod === 'Bearer Token' && apiConfig.apiKey) headers['Authorization'] = `Bearer ${apiConfig.apiKey}`;
        if (apiConfig.authMethod === 'API Key Header' && apiConfig.apiKey) headers['X-API-Key'] = apiConfig.apiKey;
        await fetch(apiConfig.endpoint, {
          method: 'POST', headers,
          body: JSON.stringify(payload),
          signal: AbortSignal.timeout((parseInt(apiConfig.timeout) || 30) * 1000),
        });
      } catch {}
    }

    // Call local backend
    try {
      const backendResp = await fetch('/api/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, amount: amt, apiConfig, sourceAccountIban: sourceAccountIban || undefined }),
      });
      if (!backendResp.ok) {
        const err = await backendResp.json().catch(() => ({}));
        if (err.error) { setSending(false); setAlert({ type: 'error', msg: err.error }); return; }
      }
    } catch (err) {
      console.warn('Backend sync:', err.message);
    }

    const debitUSD = toUSD(amt, form.currency);
    const srcAcct = accounts?.find(a => a.iban === sourceAccountIban);
    addTransaction({
      type: "API", debitAmount: debitUSD,
      description: (form.narrative || "API Transfer") + (form.currency !== 'USD' ? ` (${formatFXAmount(amt, form.currency)} @ ${getFXRate(form.currency)})` : ''),
      counterparty: form.beneficiary, beneficiary: form.beneficiary,
      accountNo: form.accountNo, bankName: form.bankName, bankBIC: form.bankBIC,
      currency: form.currency, mt: "API",
      keyTest: finalKeyTest, valueDate: form.valueDate,
      apiEndpoint: apiConfig.endpoint || "Local",
      accountIban: sourceAccountIban || null,
      accountHolder: srcAcct?.holderName || null,
    });

    setSending(false);
    setStep(4);
    setAlert({ type: "success", msg: `Transfer sent via API. Ref: ${finalRef} | Key Test: ${finalKeyTest}` });
    setSlip({
      ...form, txnRef: finalRef, method: "API Transfer", keyTest: finalKeyTest,
      apiEndpoint: apiConfig.endpoint || "Local Platform",
    });
  };

  // Reset
  const handleNewTransaction = () => {
    setForm({ beneficiary: "", accountNo: "", bankName: "", bankBIC: "", currency: "USD", amount: "", narrative: "", valueDate: todayISO() });
    setStep(apiSaved ? 2 : 1);
    setAlert(null);
  };

  // Payload for preview
  const payload = {
    transaction_ref: txRef,
    sender: COMPANY,
    beneficiary: form.beneficiary || "",
    account_no: form.accountNo || "",
    bank: form.bankName || "",
    bic: form.bankBIC || "",
    currency: form.currency,
    amount: parseFloat(form.amount) || 0,
    value_date: form.valueDate,
    narrative: form.narrative || "Transfer",
    key_test: keyTest,
    ktt_code: KTT_CODE,
  };

  // Review rows for Step 3
  const reviewRows = [
    { section: "API ENDPOINT" },
    { label: "Endpoint URL", value: apiConfig.endpoint || "(Local Platform)", mono: true },
    { label: "Base URL", value: apiConfig.baseURL || "-" },
    { label: "Server IP", value: apiConfig.serverIP || "-" },
    { label: "Auth Method", value: apiConfig.authMethod },
    { label: "API Key", value: apiConfig.apiKey ? "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022" + apiConfig.apiKey.slice(-4) : "-" },
    { label: "Private Key", value: apiConfig.privateKey ? "Configured" : "-" },
    { label: "Protocol", value: apiConfig.protocol },
    { section: "PAYMENT DETAILS" },
    { label: "Transaction Reference", value: txRef, mono: true },
    { label: "Key Test Number", value: keyTest, gold: true },
    { label: "Beneficiary", value: form.beneficiary },
    { label: "Account Number", value: form.accountNo },
    { label: "Receiver Bank", value: form.bankName || "-" },
    { label: "Bank BIC/SWIFT", value: form.bankBIC || "-" },
    { label: "Currency", value: form.currency },
    { label: "Amount", value: formatUSD(parseFloat(form.amount || 0)), highlight: true },
    { label: "Value Date", value: form.valueDate },
    { label: "Narrative", value: form.narrative || "-" },
  ];

  return (
    <div className="gap-16">
      {alert && (
        <div className={`alert alert-${alert.type}`}>
          {alert.type === "success" ? "\u2713" : "\u2717"} {alert.msg}
        </div>
      )}

      {/* STEP INDICATOR */}
      <div style={{
        display: "flex", gap: 0, background: "var(--surface)", border: "1px solid var(--border)",
        borderRadius: "var(--radius-lg)", overflow: "hidden"
      }}>
        {[
          { n: 1, label: "API Endpoint" },
          { n: 2, label: "Payment Details" },
          { n: 3, label: "Review & Edit" },
          { n: 4, label: "Confirmed" },
        ].map(s => (
          <div key={s.n} style={{
            flex: 1, padding: "14px 16px", textAlign: "center",
            background: step >= s.n ? (step === s.n ? "var(--gold-soft)" : "var(--surface2)") : "var(--surface)",
            borderRight: s.n < 4 ? "1px solid var(--border)" : "none",
            fontWeight: step === s.n ? 700 : 500,
            color: step >= s.n ? "var(--gold)" : "var(--text3)",
            fontSize: 12,
          }}>
            <span style={{ marginRight: 6, fontWeight: 700 }}>{s.n}</span>{s.label}
          </div>
        ))}
      </div>

      {/* ===== STEP 1: API ENDPOINT CONFIG ===== */}
      {step === 1 && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">API Endpoint Configuration</span>
            {apiSaved && <span className="badge badge-settled">SAVED</span>}
          </div>
          <div className="card-body">
            <div style={{ fontSize: 13, color: "var(--text2)", marginBottom: 20, lineHeight: 1.7 }}>
              Configure the API endpoint details before proceeding with the transfer.
              All connection settings must be submitted and saved first.
            </div>
            <div className="gap-16">
              <div className="form-group">
                <label className="form-label">API Endpoint URL *</label>
                <input className="form-input" placeholder="https://api.bank.com/v1/transfer" value={apiConfig.endpoint} onChange={setApi("endpoint")} />
              </div>
              <div className="grid2">
                <div className="form-group">
                  <label className="form-label">Base URL</label>
                  <input className="form-input" placeholder="https://api.bank.com" value={apiConfig.baseURL} onChange={setApi("baseURL")} />
                </div>
                <div className="form-group">
                  <label className="form-label">Server IP Address</label>
                  <input className="form-input" placeholder="e.g. 203.150.xx.xx" value={apiConfig.serverIP} onChange={setApi("serverIP")} />
                </div>
              </div>
              <div className="grid2">
                <div className="form-group">
                  <label className="form-label">Protocol</label>
                  <select className="form-input" value={apiConfig.protocol} onChange={setApi("protocol")}>
                    <option>HTTPS</option><option>HTTP</option><option>WSS</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Authentication Method</label>
                  <select className="form-input" value={apiConfig.authMethod} onChange={setApi("authMethod")}>
                    <option>Bearer Token</option><option>API Key Header</option><option>Basic Auth</option><option>OAuth 2.0</option><option>HMAC Signature</option>
                  </select>
                </div>
              </div>
              <div className="grid2">
                <div className="form-group">
                  <label className="form-label">API Key / Token</label>
                  <input className="form-input" type="password" placeholder="sk_live_xxxxxxxxx" value={apiConfig.apiKey} onChange={setApi("apiKey")} />
                </div>
                <div className="form-group">
                  <label className="form-label">Private Key / Secret</label>
                  <input className="form-input" type="password" placeholder="Private key or secret" value={apiConfig.privateKey} onChange={setApi("privateKey")} />
                </div>
              </div>
              <div className="grid2">
                <div className="form-group">
                  <label className="form-label">Webhook / Callback URL</label>
                  <input className="form-input" placeholder="https://yourserver.com/webhook" value={apiConfig.webhookURL} onChange={setApi("webhookURL")} />
                </div>
                <div className="form-group">
                  <label className="form-label">Content Type</label>
                  <select className="form-input" value={apiConfig.contentType} onChange={setApi("contentType")}>
                    <option>application/json</option><option>application/xml</option><option>multipart/form-data</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Request Timeout (seconds)</label>
                <input className="form-input" type="number" placeholder="30" value={apiConfig.timeout} onChange={setApi("timeout")} style={{ maxWidth: 200 }} />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                <button className="btn btn-gold btn-lg" onClick={handleSaveApiConfig}>
                  Save & Continue to Payment &rarr;
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== STEP 2: PAYMENT DETAILS ===== */}
      {step === 2 && (
        <div className="grid2">
          <div className="gap-16">
            {/* API Config Summary */}
            <div className="card" style={{ background: "var(--surface2)" }}>
              <div style={{ padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <span style={{ fontSize: 11, color: "var(--text3)", fontWeight: 600, letterSpacing: "0.1em" }}>API ENDPOINT: </span>
                  <span style={{ fontSize: 13, color: "var(--gold)", fontFamily: "'IBM Plex Mono', monospace" }}>
                    {apiConfig.endpoint || "Local Platform"}
                  </span>
                </div>
                <button className="btn btn-outline btn-sm" onClick={handleBackToApiConfig}>Edit Endpoint</button>
              </div>
            </div>

            {/* Key Test */}
            <div className="kt-box">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div className="kt-label">AUTO KEY TEST</div>
                  <div className="kt-value">{keyTest}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div className="kt-label">TRANSACTION REF</div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 14, color: "var(--gold)", fontWeight: 600, marginTop: 6 }}>{txRef}</div>
                </div>
              </div>
            </div>

            {/* Payment Form */}
            <div className="card">
              <div className="card-header"><span className="card-title">Payment Details</span></div>
              <div className="card-body">
                <div className="gap-16">
                  {[
                    { label: "Beneficiary Name *", key: "beneficiary", placeholder: "Enter beneficiary full legal name" },
                    { label: "Account Number *", key: "accountNo", placeholder: "Enter account number or IBAN" },
                    { label: "Receiver Bank", key: "bankName", placeholder: "Enter receiver bank name" },
                    { label: "Bank BIC/SWIFT", key: "bankBIC", placeholder: "Enter BIC/SWIFT code" },
                  ].map(f => (
                    <div key={f.key} className="form-group">
                      <label className="form-label">{f.label}</label>
                      <input className="form-input" placeholder={f.placeholder} value={form[f.key]} onChange={set(f.key)} />
                    </div>
                  ))}
                  <div className="grid2">
                    <div className="form-group">
                      <label className="form-label">Currency</label>
                      <select className="form-input" value={form.currency} onChange={set("currency")}>
                        <option>USD</option><option>EUR</option><option>GBP</option><option>THB</option><option>JPY</option><option>CHF</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Amount *</label>
                      <input className="form-input" type="number" placeholder="0.00" min="0.01" step="0.01" value={form.amount} onChange={set("amount")} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Value Date</label>
                    <input className="form-input" type="date" value={form.valueDate} onChange={set("valueDate")} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Narrative / Purpose</label>
                    <textarea className="form-input" placeholder="Payment reference or purpose" value={form.narrative} onChange={set("narrative")} />
                  </div>
                  {/* Source Account Selector */}
                  {accounts && accounts.length > 0 && (
                    <div className="form-group">
                      <label className="form-label">Source Account</label>
                      <select className="form-input" value={sourceAccountIban} onChange={e => setSourceAccountIban(e.target.value)}>
                        <option value="">Main Wallet</option>
                        {accounts.filter(a => a.status === 'ACTIVE').map(a => (
                          <option key={a.iban} value={a.iban}>
                            {a.ibanFormatted} — {a.holderName} — {formatUSD(a.balance)}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                    <span style={{ fontSize: 14, color: "var(--text2)" }}>
                      Available: <strong style={{ color: "var(--teal)", fontSize: 16 }}>{formatUSD(sourceAccountIban ? (accounts?.find(a => a.iban === sourceAccountIban)?.balance || 0) : wallet.balance)}</strong>
                    </span>
                    <button className="btn btn-gold btn-lg" onClick={handleGoToReview}>
                      Review Before Sending &rarr;
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payload Preview */}
          <div className="card" style={{ alignSelf: "start", position: "sticky", top: 0 }}>
            <div className="card-header">
              <span className="card-title">API Payload Preview</span>
              <button className="btn btn-outline btn-sm" onClick={() => navigator.clipboard.writeText(JSON.stringify(payload, null, 2))}>Copy</button>
            </div>
            <div className="card-body">
              <div className="code-block">{JSON.stringify(payload, null, 2)}</div>
            </div>
          </div>
        </div>
      )}

      {/* ===== STEP 3: REVIEW & EDIT ===== */}
      {step === 3 && (
        <div className="card" style={{ maxWidth: 720, margin: "0 auto" }}>
          <div className="card-header">
            <span className="card-title">Review All Details Before Sending</span>
            <span className="badge badge-pending" style={{ fontSize: 11, padding: "4px 12px" }}>PENDING CONFIRMATION</span>
          </div>
          <div className="card-body">
            <div style={{ fontSize: 13, color: "var(--text2)", marginBottom: 20, lineHeight: 1.7 }}>
              Verify all information below. Click <strong style={{ color: "var(--gold)" }}>"Back to Edit"</strong> to correct any details before confirming.
            </div>

            {reviewRows.map((r, i) => {
              if (r.section) {
                return (
                  <div key={i} style={{
                    fontSize: 10, fontWeight: 700, color: "var(--gold)", letterSpacing: "0.2em",
                    padding: "16px 0 8px", borderBottom: "2px solid var(--border)",
                    marginTop: i > 0 ? 12 : 0,
                  }}>
                    {r.section}
                  </div>
                );
              }
              return (
                <div key={i} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "10px 0", borderBottom: "1px solid var(--border-light)",
                }}>
                  <span style={{ fontSize: 12, color: "var(--text3)", fontWeight: 600, letterSpacing: "0.05em", minWidth: 180 }}>
                    {r.label}
                  </span>
                  <span style={{
                    fontSize: r.highlight ? 16 : 14,
                    fontWeight: r.highlight || r.gold ? 700 : 500,
                    color: r.gold ? "var(--gold)" : r.highlight ? "var(--navy)" : "var(--text)",
                    fontFamily: r.mono || r.gold ? "'IBM Plex Mono', monospace" : "inherit",
                    textAlign: "right", wordBreak: "break-all", maxWidth: "55%",
                    letterSpacing: r.gold ? "0.2em" : "normal",
                  }}>
                    {r.value}
                  </span>
                </div>
              );
            })}

            <div style={{ marginTop: 28, display: "flex", gap: 12, justifyContent: "space-between" }}>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-outline" onClick={handleBackToApiConfig}>Edit Endpoint</button>
                <button className="btn btn-outline" onClick={handleBackToPayment}>&larr; Edit Payment</button>
              </div>
              <button className="btn btn-gold btn-lg" onClick={handleConfirmSend} disabled={sending}>
                {sending ? "Processing..." : "Confirm & Send via API"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== STEP 4: CONFIRMED ===== */}
      {step === 4 && (
        <div className="card" style={{ maxWidth: 600, margin: "0 auto", textAlign: "center" }}>
          <div className="card-body" style={{ padding: 48 }}>
            <div style={{
              width: 72, height: 72, borderRadius: "50%",
              background: "linear-gradient(135deg, var(--teal), var(--teal-light))",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 24px", fontSize: 32, color: "white",
            }}>
              &#10003;
            </div>
            <h3 style={{ fontFamily: "'Source Serif 4', serif", fontSize: 24, color: "var(--navy)", marginBottom: 8 }}>
              API Transfer Sent Successfully
            </h3>
            <p style={{ color: "var(--text2)", marginBottom: 24, fontSize: 14 }}>
              Transaction recorded and payment dispatched via {apiConfig.endpoint || "Local Platform"}.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button className="btn btn-gold" onClick={handleNewTransaction}>New Transaction</button>
              <button className="btn btn-outline" onClick={() => window.print()}>Print Receipt</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
